/* KD9 — Axios client with cookie credentials, CSRF and auto-refresh. */
import axios from "axios";

// In production, keep API requests on the frontend origin and let nginx
// proxy /api to FastAPI. This preserves first-party session cookies.
const baseURL =
  process.env.NODE_ENV === "production"
    ? ""
    : process.env.REACT_APP_BACKEND_URL;

const readCookie = (name) => {
  if (typeof document === "undefined") return null;
  const prefix = `${name}=`;
  const item = document.cookie.split("; ").find((part) => part.startsWith(prefix));
  return item ? decodeURIComponent(item.slice(prefix.length)) : null;
};

const api = axios.create({
  baseURL,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const method = String(config.method || "get").toLowerCase();
  if (!["get", "head", "options"].includes(method)) {
    const csrf = readCookie("csrf_token");
    if (csrf) config.headers["X-CSRF-Token"] = csrf;
  }
  return config;
});

export const formatApiError = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Error desconocido";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((x) => (x && typeof x.msg === "string" ? x.msg : JSON.stringify(x))).join(" · ");
  if (d && typeof d.message === "string") return d.message;
  if (d && typeof d.msg === "string") return d.msg;
  return String(d);
};

let refreshPromise = null;
const REFRESH_PATH = "/api/auth/refresh";
const SAFE_TO_RETRY = (cfg) => cfg && !cfg.__isRetry && !String(cfg.url || "").includes(REFRESH_PATH);

export const expireFrontendSession = (reason = "expired", broadcast = true) => {
  if (typeof window === "undefined") return;
  ["kd9-server-permissions", "kd9-server-flags", "kd9-role", "kd9-flags", "kd9-requests"]
    .forEach((key) => window.localStorage.removeItem(key));
  if (broadcast) {
    try {
      window.localStorage.setItem(
        "kd9-session-terminated",
        JSON.stringify({ at: Date.now(), reason })
      );
    } catch (_) {}
  }
  window.dispatchEvent(new CustomEvent("kd9-session-expired"));
  if (window.location.pathname.startsWith("/app")) {
    if (reason === "inactivity") {
      window.location.replace("/login?reason=inactivity");
    } else {
      window.location.replace("/login");
    }
  }
};

api.interceptors.response.use(
  (response) => {
    const method = String(response?.config?.method || "get").toLowerCase();
    if (typeof window !== "undefined" && !["get", "head", "options"].includes(method)) {
      window.dispatchEvent(new CustomEvent("kd9-energy-maybe-changed"));
    }
    return response;
  },
  async (error) => {
    const cfg = error?.config;
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    if (status === 401 && detail?.code === "SESSION_INACTIVITY_TIMEOUT") {
      expireFrontendSession("inactivity");
      return Promise.reject(error);
    }
    if (status === 403 && detail?.code === "ACCOUNT_BLOCKED") {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("kd9-account-blocked", {
          detail: { message: detail.message },
        }));
      }
      return Promise.reject(error);
    }
    if (status === 401 && SAFE_TO_RETRY(cfg)) {
      try {
        if (!refreshPromise) {
          refreshPromise = api.post(REFRESH_PATH).finally(() => {
            refreshPromise = null;
          });
        }
        await refreshPromise;
        cfg.__isRetry = true;
        return api(cfg);
      } catch (_) {
        expireFrontendSession();
        return Promise.reject(error);
      }
    }
    if (status === 401) expireFrontendSession();
    return Promise.reject(error);
  }
);

export default api;
